import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import api from '../../services/api';

interface Company {
  id: number;
  name: string;
  status: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  department: string | null;
  is_company_admin: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  is_active: boolean;
  is_archived: boolean;
  archived_at: string | null;
  date_joined: string;
  last_login: string | null;
  company_name: string | null;
  company_id: number | null;
  role: string;
  credit_balance: number;
  user_type: string;
  usermanager: boolean;
  total_tokens?: number;
  total_token_cost?: number;
}

interface UserFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  department: string | null;
  password: string;
  confirm_password: string;
  user_type: 'individual' | 'company_admin' | 'super_admin';
  company_id: number | null;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  usermanager: boolean;
}

interface FormErrors {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  department?: string;
  password?: string;
  confirm_password?: string;
  user_type?: string;
  company_id?: string;
  is_active?: string;
  is_staff?: string;
  is_superuser?: string;
  usermanager?: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    department: null,
    password: '',
    confirm_password: '',
    user_type: 'individual',
    company_id: null,
    is_active: true,
    is_staff: false,
    is_superuser: false,
    usermanager: true,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userTypes = [
    { value: 'individual', label: 'Individual User', icon: <PersonIcon /> },
    { value: 'company_admin', label: 'Company Admin', icon: <BusinessIcon /> },
    { value: 'super_admin', label: 'Super Admin', icon: <AdminIcon /> },
    { value: 'candidate', label: 'Candidate', icon: <PersonIcon /> },
  ];

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (userTypeFilter !== 'all') {
        params.append('user_type', userTypeFilter);
      }

      if (statusFilter !== 'all') {
        if (statusFilter === 'active') {
          params.append('is_active', 'true');
        } else if (statusFilter === 'inactive') {
          params.append('is_active', 'false');
        } else if (statusFilter === 'archived') {
          params.append('is_archived', 'true');
        }
      }

      if (companyFilter !== 'all') {
        params.append('company', companyFilter);
      }

      const response = await api.get(`/super-admin/users/?${params}`);
      console.log('Users response:', response.data);

      setUsers(response.data.results || response.data);
      setTotalCount(response.data.count || response.data.length);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(
        error.response?.data?.error ||
        error.message ||
        'Failed to fetch users'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/super-admin/companies/');
      setCompanies(response.data.results || response.data);
    } catch (error: any) {
      console.error('Error fetching companies:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, [page, rowsPerPage, searchTerm, userTypeFilter, statusFilter, companyFilter]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleUserTypeFilterChange = (event: any) => {
    setUserTypeFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleCompanyFilterChange = (event: any) => {
    setCompanyFilter(event.target.value);
    setPage(0);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      department: null,
      password: '',
      confirm_password: '',
      user_type: 'individual',
      company_id: null,
      is_active: true,
      is_staff: false,
      is_superuser: false,
      usermanager: true,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      department: user.department,
      password: '',
      confirm_password: '',
      user_type: user.user_type as 'individual' | 'company_admin' | 'super_admin',
      company_id: user.company_id,
      is_active: user.is_active,
      is_staff: user.is_staff,
      is_superuser: user.is_superuser,
      usermanager: (user as any).usermanager ?? true,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleArchiveUser = async (userId: number, archive: boolean) => {
    const action = archive ? 'archive' : 'unarchive';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await api.patch(`/super-admin/users/${userId}/`, {
        is_archived: archive,
        archived_at: archive ? new Date().toISOString() : null,
      });
      fetchUsers();
    } catch (error: any) {
      console.error(`Error ${action}ing user:`, error);
      alert(`Failed to ${action} user: ` + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/super-admin/users/${userId}/`);
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + (error.response?.data?.error || error.message));
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.username?.trim()) {
      errors.username = 'Username is required';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }

    // Password validation for new users
    if (!editingUser) {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      }

      if (formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    } else {
      // For editing, validate password only if provided
      if (formData.password && formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      }

      if (formData.password && formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    }

    // Company validation for company admins
    if (formData.user_type === 'company_admin' && !formData.company_id) {
      errors.company_id = 'Company is required for company admins';
    }

    // Super admin validation
    if (formData.user_type === 'super_admin') {
      if (!formData.is_staff) {
        errors.is_staff = 'Super admins must be staff members';
      }
      if (!formData.is_superuser) {
        errors.is_superuser = 'Super admins must have superuser privileges';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Prepare data for submission (usermanager is stored in interviews_usercompanyrelationship)
      const submitData: any = {
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        department: formData.department,
        user_type: formData.user_type,
        company_id: formData.company_id,
        is_active: formData.is_active,
        is_staff: formData.is_staff,
        is_superuser: formData.is_superuser,
        usermanager: formData.usermanager,
      };

      // Only include password if provided (for new users or password changes)
      if (formData.password) {
        submitData.password = formData.password;
      }

      if (editingUser) {
        await api.put(`/super-admin/users/${editingUser.id}/`, submitData);
      } else {
        await api.post('/super-admin/users/', submitData);
      }

      setDialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save user';

      // Handle field-specific errors
      if (error.response?.data) {
        const fieldErrors: FormErrors = {};
        Object.keys(error.response.data).forEach(key => {
          if (key in formData) {
            fieldErrors[key as keyof FormErrors] = Array.isArray(error.response.data[key])
              ? error.response.data[key][0]
              : error.response.data[key];
          }
        });
        setFormErrors(fieldErrors);
      } else {
        alert(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = (field: keyof UserFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getUserTypeIcon = (userType: string) => {
    switch (userType) {
      case 'individual':
        return <PersonIcon fontSize="small" />;
      case 'company_admin':
        return <BusinessIcon fontSize="small" />;
      case 'super_admin':
        return <AdminIcon fontSize="small" />;
      case 'candidate':
        return <PersonIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const getUserTypeColor = (userType: string) => {
    switch (userType) {
      case 'individual':
        return 'default';
      case 'company_admin':
        return 'primary';
      case 'super_admin':
        return 'error';
      case 'candidate':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (user: User) => {
    if (user.is_archived) return 'error';
    if (!user.is_active) return 'warning';
    return 'success';
  };

  const getStatusText = (user: User) => {
    if (user.is_archived) return 'Archived';
    if (!user.is_active) return 'Inactive';
    return 'Active';
  };

  const formatJoinedDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    return `from ${month} ${day}' ${year}`;
  };

  const isSubmitDisabled = () => {
    if (submitting) return true;

    // For new users, passwords are required and must match
    if (!editingUser) {
      if (!formData.password || !formData.confirm_password) return true;
      if (formData.password !== formData.confirm_password) return true;
    }

    // For editing users, only require password match when at least one field has a value
    if (editingUser && (formData.password || formData.confirm_password)) {
      if (formData.password !== formData.confirm_password) return true;
    }

    return false;
  };

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Users Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all users in the system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
        >
          Add User
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userTypeFilter}
                  onChange={handleUserTypeFilterChange}
                  label="User Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {userTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={companyFilter}
                  onChange={handleCompanyFilterChange}
                  label="Company"
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  {companies.map(company => (
                    <MenuItem key={company.id} value={company.id.toString()}>{company.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchUsers} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Tokens</TableCell>
                <TableCell>AI Cost</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {user.first_name} {user.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        icon={getUserTypeIcon(user.user_type)}
                        label={userTypes.find(t => t.value === user.user_type)?.label || user.user_type}
                        color={getUserTypeColor(user.user_type) as any}
                        size="small"
                        variant={user.user_type === 'candidate' ? 'outlined' : 'filled'}
                        sx={user.user_type === 'candidate' ? { borderColor: 'success.light', color: 'success.dark' } : undefined}
                      />
                      {user.user_type === 'company_admin' && (
                        <Chip
                          label={user.is_staff ? 'Staff' : 'Not staff'}
                          size="small"
                          variant="outlined"
                          color={user.is_staff ? 'success' : 'default'}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {user.company_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {formatJoinedDate(user.date_joined)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(user)}
                      color={getStatusColor(user) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.credit_balance.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(user.total_tokens || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="error.main">
                      ${(user.total_token_cost || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {user.is_archived ? (
                      <Tooltip title="Unarchive">
                        <IconButton
                          size="small"
                          onClick={() => handleArchiveUser(user.id, false)}
                          color="success"
                        >
                          <UnarchiveIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Archive">
                        <IconButton
                          size="small"
                          onClick={() => handleArchiveUser(user.id, true)}
                          color="warning"
                        >
                          <ArchiveIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => handleFormChange('username', e.target.value)}
                error={!!formErrors.username}
                helperText={formErrors.username}
                required
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
                error={!!formErrors.last_name}
                helperText={formErrors.last_name}
                required
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department || ''}
                onChange={(e) => handleFormChange('department', e.target.value || null)}
                error={!!formErrors.department}
                helperText={formErrors.department}
              />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={formData.user_type}
                  onChange={(e) => handleFormChange('user_type', e.target.value)}
                  label="User Type"
                  error={!!formErrors.user_type}
                >
                  {userTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {formData.user_type === 'company_admin' && (
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <FormControl fullWidth error={!!formErrors.company_id}>
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={formData.company_id?.toString() || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleFormChange('company_id', value ? parseInt(value, 10) : null);
                    }}
                    label="Company"
                  >
                    {companies.map(company => (
                      <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {!editingUser && (
              <>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={(e) => handleFormChange('confirm_password', e.target.value)}
                    error={!!formErrors.confirm_password || !!(formData.password && formData.confirm_password && formData.password !== formData.confirm_password)}
                    helperText={
                      formErrors.confirm_password || 
                      (formData.password && formData.confirm_password && formData.password === formData.confirm_password
                        ? 'Passwords match'
                        : formData.password && formData.confirm_password && formData.password !== formData.confirm_password
                        ? 'Passwords do not match'
                        : '')
                    }
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {formData.password && formData.confirm_password ? (
                            formData.password === formData.confirm_password ? (
                              <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                            ) : (
                              <Cancel sx={{ color: 'error.main', mr: 1 }} />
                            )
                          ) : null}
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </>
            )}
            {editingUser && (
              <>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="New Password (optional)"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={(e) => handleFormChange('confirm_password', e.target.value)}
                    error={!!formErrors.confirm_password || !!(formData.password && formData.confirm_password && formData.password !== formData.confirm_password)}
                    helperText={
                      formErrors.confirm_password || 
                      (formData.password && formData.confirm_password && formData.password === formData.confirm_password
                        ? 'Passwords match'
                        : formData.password && formData.confirm_password && formData.password !== formData.confirm_password
                        ? 'Passwords do not match'
                        : '')
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {formData.password && formData.confirm_password ? (
                            formData.password === formData.confirm_password ? (
                              <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                            ) : (
                              <Cancel sx={{ color: 'error.main', mr: 1 }} />
                            )
                          ) : null}
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </>
            )}
            <Box sx={{ flex: '1 1 100%' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_staff}
                    onChange={(e) => handleFormChange('is_staff', e.target.checked)}
                  />
                }
                label="Staff Member"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_superuser}
                    onChange={(e) => handleFormChange('is_superuser', e.target.checked)}
                  />
                }
                label="Super User"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.usermanager}
                    onChange={(e) => handleFormChange('usermanager', e.target.checked)}
                  />
                }
                label="User Manager"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            type="button"
            onClick={(e) => { e.preventDefault(); handleSubmit(); }}
            variant="contained"
            disabled={isSubmitDisabled()}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            {submitting ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
